import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { MyPledgeResponse, PublicStats } from "../api/types";
import { money } from "../lib/format";

const SEEN_KEY = "ride4baker:fundraiser-closed-seen";

/**
 * Site-wide notice shown once the fundraiser is closed. Mounted once in Chrome,
 * so it fetches `/public/stats` a single time per session and renders nothing
 * while the fundraiser is still open. When closed, signed-in donors also get
 * their owed amount from `/pledges/me`.
 */
export function FundraiserBanner() {
  const { user } = useAuth();
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [mine, setMine] = useState<MyPledgeResponse | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(SEEN_KEY) === "1");

  useEffect(() => {
    api.get<PublicStats>("/public/stats").then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    if (stats?.closed && user) {
      api.get<MyPledgeResponse>("/pledges/me").then(setMine).catch(() => {});
    }
  }, [stats?.closed, user]);

  if (!stats?.closed) return null;

  const gofundme = stats.gofundme || mine?.gofundme || "";
  const owed = mine?.pledge ? mine.pledge.owedNow : null;
  const miles = stats.miles;

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setDismissed(true);
  };

  return (
    <>
      {/* Only pledgers get the one-time modal; anonymous visitors just see the
          banner. An unprompted donate popup reads as deceptive to crawlers. */}
      {!dismissed && owed != null && (
        <ClosedModal owed={owed} miles={miles} gofundme={gofundme} onDismiss={dismiss} />
      )}
      <ClosedBanner owed={owed} gofundme={gofundme} />
    </>
  );
}

function ClosedBanner({ owed, gofundme }: { owed: number | null; gofundme: string }) {
  return (
    <div
      style={{
        background: "var(--ink)",
        color: "var(--bg)",
        padding: "10px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        flexWrap: "wrap",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      <span>
        {owed != null
          ? `Scott's ride is complete — your donation total is ${money(owed)}.`
          : "Scott's ride is complete — thank you!"}
      </span>
      {gofundme && (
        <a
          href={gofundme}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 700, whiteSpace: "nowrap" }}
        >
          Donate on GoFundMe →
        </a>
      )}
    </div>
  );
}

function ClosedModal({
  owed,
  miles,
  gofundme,
  onDismiss,
}: {
  owed: number | null;
  miles: number;
  gofundme: string;
  onDismiss: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onDismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          borderRadius: 20,
          padding: "32px 28px",
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ fontSize: 40, lineHeight: 1 }}>🏁</div>
        <h2 style={{ margin: "14px 0 0", fontFamily: "var(--display)", fontSize: 28, fontWeight: 400, letterSpacing: -0.6 }}>
          The Ride is Complete!
        </h2>
        <p style={{ marginTop: 10, color: "var(--ink-soft)", fontSize: 14 }}>
          Scott rode {Math.round(miles).toLocaleString()} miles this summer for kids with eye cancer.
        </p>

        {owed != null && (
          <div style={{ marginTop: 18, padding: 18, background: "var(--accent-tint)", borderRadius: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Your donation total
            </div>
            <div style={{ marginTop: 4, fontFamily: "var(--display)", fontSize: 44, fontWeight: 400, color: "var(--accent)", letterSpacing: -1.5, lineHeight: 1 }}>
              {money(owed)}
            </div>
          </div>
        )}

        {gofundme && (
          <a
            href={gofundme}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              marginTop: 20,
              padding: "14px 22px",
              background: "var(--accent)",
              color: "var(--accent-ink)",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 15,
              textDecoration: "none",
            }}
          >
            Donate on GoFundMe →
          </a>
        )}

        <button
          onClick={onDismiss}
          style={{
            marginTop: 12,
            background: "none",
            border: "none",
            color: "var(--ink-soft)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            padding: 8,
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
